/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: chen
 *  创建时间: 2026-08-14 11:11
 *  文件名: src/pages/resource-preview/types.ts
 *  功能描述:
 */

export interface ResourceTypeItem {
  code: string;
  name: string;
  value: number;
}
export interface ResourceFile {
  extension: string;
  md5: string;
  fileId: string;
  basicResourceId: string;
  fileName: string;
}
export interface BasicResourceItem {
  id: string;
  name: string;
  files: ResourceFile[];
  lives: any[];
  resourceType: ResourceTypeItem;
  [key: string]: any;
}

export interface InternalResourceItem {
  id: string;
  name: string;
  fileInfos: Array<{
    extension: string;
    md5: string;
    fileId: string;
    basicResourceId: string;
    fileName: string;
  }>;
  resourceType: string;
}

// 备课资料详情
export interface GetBasicResourceItemResponse extends BasicResourceItem {
  chapters: Array<{
    chapterId: string;
    preparationId: string;
    chapterPath: string; // 以_分割
  }>;
  associationResources: BasicResourceItem[];
}

export interface RelationshipResourceItem extends BasicResourceItem {
  id: string;
  resourceId: string;
  resourceType: ResourceTypeItem;
  relationships: BasicResourceItem[];
}

// 课堂资料详情
export interface GetClassRecordResourceItemResponse {
  id: string;
  name: string;
  applyStartTime: number;
  teacherName: string;
  teacherId: number;
  materials: BasicResourceItem[];
  relationshipResources: RelationshipResourceItem[];
}

// 子资源类型
export enum FollowSubResourceType {
  Courseware = 'courseware', // 课件
  CourseMemoir = 'courseMemoir', // 课堂实录
  Assign = 'assign', // 作业
  Preview = 'preview', // 预习
  Exam = 'exam', // 试卷
  TeachPlan = 'teachPlan', // 教案
  PrepareLessonWare = 'prepareLessonWare', // 备课课件
  PrepareCourseMemoir = 'prepareCourseMemoir', // 备课实录
  Summary = 'summary', // 备课纪要
  Material = 'material', // 素材,
  Live = 'live', // 直播，直播中、直播预告 业务code
}

export const FollowSubResourceTypes = {
  COURSEWARE: FollowSubResourceType.Courseware,
  COURSE_WARE: FollowSubResourceType.Courseware,
  COURSE_MEMOIR: FollowSubResourceType.CourseMemoir,
  MODEL_COURSE: FollowSubResourceType.CourseMemoir,
  GENERAL_ASSIGNMENT: FollowSubResourceType.Assign,
  GENERAL_PREVIEW: FollowSubResourceType.Preview,
  GENERAL_PAPER: FollowSubResourceType.Exam,
  TEACHING_PLAN: FollowSubResourceType.TeachPlan,
  PREPARE_LESSON_WARE: FollowSubResourceType.PrepareLessonWare,
  PREPARE_LESSON_MEMOIR: FollowSubResourceType.PrepareCourseMemoir,
  SUMMARY: FollowSubResourceType.Summary,
  MATERIAL: FollowSubResourceType.Material,
  LIVE: FollowSubResourceType.Live,
};

export enum ResourceType {
  /**
   * 普通实录(1)
   */
  COURSE_MEMOIR = 'COURSE_MEMOIR',
  /**
   * 教学课件(2)
   */
  COURSE_WARE = 'COURSE_WARE',
  /**
   * 素材(3)
   */
  MATERIAL = 'MATERIAL',
  /**
   * 教案(4)
   */
  TEACHING_PLAN = 'TEACHING_PLAN',
  /**
   * 示范课(7)
   */
  MODEL_COURSE = 'MODEL_COURSE',
  /**
   * 备课实录(8)
   */
  PREPARE_LESSON_MEMOIR = 'PREPARE_LESSON_MEMOIR',
  /**
   * 微课(9)
   */
  MICRO_COURSE = 'MICRO_COURSE',
  /**
   * 普通试卷(6)
   */
  DOCUMENT_PAPER = 'DOCUMENT_PAPER',
  /**
   * 试题(-1)
   */
  QUESTION = 'QUESTION',
  /**
   * 作业(1)
   */
  GENERAL_ASSIGNMENT = 'GENERAL_ASSIGNMENT',
  /**
   * 试卷(4) (电子试卷)
   */
  GENERAL_PAPER = 'GENERAL_PAPER',
  /**
   * 预习(2)
   */
  GENERAL_PREVIEW = 'GENERAL_PREVIEW',
  /**
   * 课中题单(8)
   */
  GENERAL_BLACKBOARD_QUESTIONNAIRE = 'GENERAL_BLACKBOARD_QUESTIONNAIRE',
  /**
   * 微课题单(16)
   */
  GENERAL_MICRO_COURSE_QUESTIONNAIRE = 'GENERAL_MICRO_COURSE_QUESTIONNAIRE',
  /**
   * 普通题单(16)
   */
  GENERAL_QUESTIONNAIRE = 'GENERAL_QUESTIONNAIRE',
  /**
   * 备课包(-1)
   */
  PREPARE_LESSON_PACKAGE = 'PREPARE_LESSON_PACKAGE',
  /**
   * 教研实录(10)
   */
  TEACHING_RESEARCH_RECORD = 'TEACHING_RESEARCH_RECORD',
  /**
   * 备课课件(20)
   */
  PREPARE_LESSON_WARE = 'PREPARE_LESSON_WARE',
  /**
   * 教研课件(22)
   */
  TEACHING_RESEARCH_WARE = 'TEACHING_RESEARCH_WARE',
  /**
   * 学习资料(14)
   */
  TEXT_RESOURCE = 'TEXT_RESOURCE',
  /**
   * 公告(25)
   */
  NOTICE = 'NOTICE',
  /**
   * 学生范例(27)
   */
  STUDENT_SAMPLE = 'STUDENT_SAMPLE',
  /**
   * 活动方案(28)
   */
  ACTIVITY_PLAN = 'ACTIVITY_PLAN',
  /**
   * 课外拓展(29)
   */
  EXTRACURRICULAR_EXTENSION = 'EXTRACURRICULAR_EXTENSION',
  /**
   * 活动实录(30)
   */
  ACTIVITY_RECORD = 'ACTIVITY_RECORD',
  /**
   * 文稿(31)
   */
  DOCUMENT = 'DOCUMENT',
  /**
   * 纪要（教研纪要、备课纪要、课堂纪要）(26)
   */
  SUMMARY = 'SUMMARY',

  /**
   * 主题课
   */
  THEME_LESSON = 'THEME_LESSON',
}

// 视频类资源
export const VIDEO_TYPES = [
  ResourceType.COURSE_MEMOIR,
  ResourceType.PREPARE_LESSON_MEMOIR,
  ResourceType.MODEL_COURSE,
  ResourceType.ACTIVITY_RECORD,
  ResourceType.TEACHING_RESEARCH_RECORD,
  ResourceType.MICRO_COURSE,
  ResourceType.THEME_LESSON,
];

// 定义资源类型的顺序，
export const ResourceTypeOrder = [
  ...VIDEO_TYPES,
  ResourceType.PREPARE_LESSON_WARE,
  ResourceType.TEACHING_RESEARCH_WARE,
  ResourceType.COURSE_WARE,
  ResourceType.TEACHING_PLAN,
  ResourceType.GENERAL_PREVIEW,
  ResourceType.GENERAL_ASSIGNMENT,
  ResourceType.ACTIVITY_PLAN,
  ResourceType.STUDENT_SAMPLE,
  ResourceType.EXTRACURRICULAR_EXTENSION,
  ResourceType.SUMMARY,
  ResourceType.QUESTION,
  ResourceType.DOCUMENT,
  ResourceType.GENERAL_PAPER,
  ResourceType.MATERIAL,
  ResourceType.DOCUMENT_PAPER,
  ResourceType.GENERAL_BLACKBOARD_QUESTIONNAIRE,
  ResourceType.GENERAL_MICRO_COURSE_QUESTIONNAIRE,
  ResourceType.GENERAL_QUESTIONNAIRE,
  ResourceType.PREPARE_LESSON_PACKAGE,
  ResourceType.TEXT_RESOURCE,
  ResourceType.NOTICE,
];
